import { useEffect, useRef, useCallback, useState } from 'react';

const POLL_INTERVAL = 60000; // 1 minute
const REMIND_INTERVAL = 180000; // 3 minutes
const DEPLOY_GRACE_PERIOD = 600000; // 10 minutes — wait for backend to finish deploying
const UPDATE_SUCCESS_KEY = '__version_update_success';
let initialVersion = null;

/**
 * Filters release notes by portal.
 * Supports both old format (string) and new format (array with portal targeting).
 *
 * New format example:
 * {
 *   "notes": [
 *     { "portals": ["all"], "items": ["General update"] },
 *     { "portals": ["admin"], "items": ["Admin-only feature"] },
 *     { "portals": ["bureau", "admin"], "items": ["Shared feature"] }
 *   ]
 * }
 *
 * @param {object} notesData - The parsed release-notes.json data
 * @param {string|null} portal - The current user's portal (e.g., 'admin', 'client', 'bureau')
 * @returns {string|null} - Filtered notes as a bullet-point string, or null if no notes
 */
const filterNotesByPortal = (notesData, portal) => {
  // Safety check: ensure notesData is an object
  if (!notesData || typeof notesData !== 'object') {
    return null;
  }

  // Backwards compatibility: if notes is a string, return as-is (shows to all)
  if (typeof notesData.notes === 'string') {
    return notesData.notes;
  }

  // If notes is an array, filter by portal
  if (Array.isArray(notesData.notes)) {
    const relevantNotes = notesData.notes
      .filter(
        (note) =>
          note.portals &&
          (note.portals.includes('all') || note.portals.includes(portal))
      )
      .flatMap((note) => note.items || []);

    // Return as bullet-point string for compatibility with UpdateBanner
    return relevantNotes.length > 0
      ? relevantNotes.map((item) => `- ${item}`).join('\n')
      : null;
  }

  // Fallback for other formats
  return notesData.notes || notesData.message || null;
};

/**
 * Polls /version.json to detect new deployments.
 * When a new frontend version is detected, waits 10 minutes before showing
 * the update banner to give the backend time to finish deploying.
 *
 * For frontend-only deploys (deployType === "frontend-only"), the banner
 * shows immediately since there are no backend changes to wait for.
 *
 * - "Update Later" dismisses the banner; it reappears every 3 minutes.
 * - "Update Now" sets a flag in sessionStorage, reloads, and the caller
 *    shows a success toast on the fresh page load.
 *
 * @param {string|null} portal - The current user's portal for filtering release notes (e.g., 'admin', 'client', 'bureau')
 * @returns {{ versionInfo: object|null, dismiss: function, updateNow: function, justUpdated: boolean, clearJustUpdated: function }}
 */
export function useVersionCheck(portal = null) {
  const hasDetected = useRef(false);
  const remindTimer = useRef(null);
  const graceTimer = useRef(null);
  const versionDataRef = useRef(null);
  const [versionInfo, setVersionInfo] = useState(null);
  const [justUpdated, setJustUpdated] = useState(false);

  // Check on mount if we just came back from an "Update Now" reload
  useEffect(() => {
    const flag = sessionStorage.getItem(UPDATE_SUCCESS_KEY);
    if (flag) {
      sessionStorage.removeItem(UPDATE_SUCCESS_KEY);
      setJustUpdated(true);
    }
  }, []);

  // Clear the justUpdated flag (called after showing the toast once)
  const clearJustUpdated = useCallback(() => {
    setJustUpdated(false);
  }, []);

  const dismiss = useCallback(() => {
    setVersionInfo(null);
    // Re-show banner after 3 minutes
    if (remindTimer.current) clearTimeout(remindTimer.current);
    remindTimer.current = setTimeout(() => {
      if (versionDataRef.current) {
        setVersionInfo({ ...versionDataRef.current });
      }
    }, REMIND_INTERVAL);
  }, []);

  const updateNow = useCallback(async () => {
    sessionStorage.setItem(UPDATE_SUCCESS_KEY, 'true');

    // Clear browser Cache API to ensure fresh resources after deploy
    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      } catch {
        // Cache API not available or failed, proceed anyway
      }
    }

    // Force a cache-busting navigation instead of standard reload.
    // Standard reload may serve HTTP-cached JS chunks; a fresh navigation
    // with a unique query param guarantees the browser fetches everything new.
    const url = window.location.pathname + '?_v=' + Date.now();
    window.location.href = url;
  }, []);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (remindTimer.current) clearTimeout(remindTimer.current);
      if (graceTimer.current) clearTimeout(graceTimer.current);
    };
  }, []);

  const check = useCallback(async () => {
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const version = data.version;

      if (!initialVersion) {
        initialVersion = version;
        return;
      }

      if (version !== initialVersion && !hasDetected.current) {
        hasDetected.current = true;

        // Attempt to fetch release notes (filtered by portal)
        let notes = null;
        try {
          const notesRes = await fetch('/release-notes.json', { cache: 'no-store' });
          if (notesRes.ok) {
            const text = await notesRes.text();
            try {
              const notesData = JSON.parse(text);
              notes = filterNotesByPortal(notesData, portal);
            } catch {
              // Invalid JSON, ignore
            }
          }
        } catch {
          // No release notes available
        }

        const info = {
          version,
          tag: data.tag || null,
          buildTime: data.buildTime || null,
          notes,
        };

        versionDataRef.current = info;

        // Frontend-only deploy — show banner immediately (no backend changes to wait for)
        const deployType = data.deployType || 'full';
        if (deployType === 'frontend-only') {
          setVersionInfo(info);
          return;
        }

        // Full deploy — wait 10 minutes for backend to finish deploying, then show banner
        graceTimer.current = setTimeout(() => {
          setVersionInfo(info);
        }, DEPLOY_GRACE_PERIOD);
      }
    } catch {
      // Network error or version.json not available, ignore
    }
  }, [portal]);

  useEffect(() => {
    // Only poll in production and staging
    const env = process.env.REACT_APP_ENV || process.env.NODE_ENV;
    if (env !== 'production' && env !== 'staging') {
      return;
    }

    check();
    const id = setInterval(check, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [check]);

  return { versionInfo, dismiss, updateNow, justUpdated, clearJustUpdated };
}
